import ContactLink from "@/components/ContactLink";
import { contactMailto } from "@/lib/contact";
import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import RewardRow from "@/components/schools/rewards/RewardRow";
import { STUDENT_PRIZE_TEMPLATES } from "@/help/fixtures";

/**
 * The Student prizes page's list, rendered with the real RewardRow so
 * the help centre matches the app.
 */
function StudentPrizesPreview() {
  return (
    <Card>
      <CardContent className="space-y-3 pt-6 md:pt-8">
        <CardDescription>
          Prizes each student can win on their own. Teachers mark them handed out from their classroom page.
        </CardDescription>
        <ul className="space-y-2">
          {STUDENT_PRIZE_TEMPLATES.map((p) => <RewardRow key={p.id} reward={p} locked />)}
        </ul>
        <Button variant="outline" className="w-full">+ Add a student prize</Button>
      </CardContent>
    </Card>
  );
}

export default function SettingStudentPrizes() {
  return (
    <>
      <p>
        A <strong>student prize</strong> is something each student earns on their own when they reach a number. "Read 500 minutes, get a fidget spinner." When a student crosses the line, the teacher gets a heads-up so they can hand out the prize.
      </p>
      <p>
        That's different from <Link href="/help/articles/setting-school-milestones">school rewards</Link>, which unlock for everyone when the whole school hits a number. Student prizes are about each student racing themselves.
      </p>

      <h2>Setting up a prize</h2>
      <p>
        From your dashboard sidebar, click <strong>Student prizes</strong>. With no prizes yet, we suggest three reading tiers sized to your campaign's length; or click <strong>Add a student prize</strong>.
      </p>
      <Screenshot caption="The student prizes page">
        <StudentPrizesPreview />
      </Screenshot>
      <p>
        Name the prize, choose how students win it, pick whether it's counted by <strong>money they raise</strong> or <strong>minutes they read</strong>, and set the amount. Give it a specific name: "Fidget spinner" gets a student more excited than "small toy."
      </p>
      <p>
        Higher tiers stack — a student who reads 1,500 minutes earns both the 500-minute prize and the 1,500-minute one. And if a student has already passed the line when you add a new prize, they earn it right away.
      </p>

      <p>
        Have a kind of prize in mind that PagePledge doesn't offer? <a href={contactMailto("Feature Request")}>Email us a feature request</a>. The same link is under your list of prizes.
      </p>

      <Callout variant="tip" title="Two or three prizes is plenty">
        <p>
          One that almost everyone can reach, one that's a stretch, and one that's a real achievement. Any more and it gets confusing; any fewer and motivation drops once kids clear the first one.
        </p>
      </Callout>

      <h2>Top student prizes</h2>
      <p>
        Choose <strong>Top student</strong> under "How students win it" to give a prize to the student who reads the most minutes (or raises the most money). Under "Who competes", pick <strong>Each class</strong> for a winner in every classroom, <strong>Each division</strong> for one winner per division, or <strong>Whole school</strong> for a single winner. Set each classroom's division on its Edit page. Want both a top reader and a top fundraiser? Add two prizes.
      </p>
      <ul>
        <li><strong>Ties all win.</strong> If two students finish tied for first, both get the prize.</li>
        <li><strong>Winners are final when the campaign finishes.</strong> Until then, teachers and the prize report show the current leader. Parents only see their own child's place, never another student's name, and teachers only see names from their own class.</li>
        <li><strong>Minimum to qualify</strong> defaults to 1, so a class where nobody logged anything has no winner. Raise it if you want the prize to take real effort.</li>
      </ul>
      <p>
        Once the campaign finishes, each winner shows up on their teacher's prize tracker with a printable certificate, ready to hand out.
      </p>

      <h2>Prize draws</h2>
      <p>
        Choose <strong>Prize draw</strong> to give students one entry for every so many minutes read (or dollars raised). After reading ends, open the <strong>Prize report</strong> and draw a winner — from the whole school, or one draw per grade band by choosing classrooms. To draw a winner in each division, set "Draw winners from" to <strong>Each division</strong>; the prize report then has a draw button for every division. You can draw early, but entries keep growing until reading ends.
      </p>

      <Callout variant="note" title="Removing a prize removes its history">
        <p>
          If students have already earned a prize, removing it also deletes their record of earning it and any draw winners. The remove button tells you how many before it asks. Once anyone has earned a prize, how it's won can't change.
        </p>
      </Callout>

      <h2>Earning vs. handing out</h2>
      <p>
        We track these as two separate steps so prizes don't slip through the cracks. The moment a student crosses the line, they've <em>earned</em> the prize — but a teacher still needs to mark it as actually handed out in real life. Until they do, the student stays on the teacher's prize tracker as "waiting." See <Link href="/help/articles/handing-out-student-prizes">Handing out student prizes</Link>.
      </p>

      <Callout variant="note" title="Once a student earns a prize, they keep it">
        <p>
          Even if a teacher undoes a reading log that pushed a student past the line, the prize stays earned. Pulling a "you earned it!" announcement back from a child is the wrong move. (If you genuinely need to clear one — say, a duplicate by accident — email <ContactLink>hello@pagepledge.com</ContactLink> and we'll help.)
        </p>
      </Callout>

      <h2>Tracking who's earned what</h2>
      <p>
        Click <strong>Prize report</strong> on the Student prizes page to see every prize broken down by classroom — who's earned each one, who's still waiting, and who's been handed it. Teachers see the same for their own class on their prize tracker.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/handing-out-student-prizes">Handing out student prizes</Link>.</li>
        <li><Link href="/help/articles/setting-school-milestones">School rewards</Link>.</li>
        <li><Link href="/help/articles/setting-classroom-goals-and-milestones">Classroom goals and rewards</Link>.</li>
      </ul>
    </>
  );
}
